import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  buildPath,
  buildTemplatePath,
  pn,
} from "@compass/core";

import { ListFooter } from "components";
import { Permission, SchoolListDto, SchoolListFilter, SchoolListResource } from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { SchoolFilter } from "../SchoolFilter";
import { SchoolListPageHeader } from "./SchoolListPageHeader";
import { SchoolTable } from "./SchoolTable";

const routeName = "schools";

export const schoolListPageRoute: PageRoute = {
  handle: "Schools",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const SchoolListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<SchoolListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<SchoolListFilter>({
      sortObjects: [{ property: pn<SchoolListDto>("name"), isDesc: false }],
      schoolFilter: { inactive: false },
  });
  const [initListFilter] = useState<SchoolListFilter>(listFilter);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<SchoolListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.schools.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.schools.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);

    ApiService.schools.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  const listFilterNode = useListFilter<SchoolListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <SchoolFilter
        filter={listFilter.schoolFilter || {}}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.schoolFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page header={<SchoolListPageHeader rootPermission={ROOT_PERMISSION} handle={schoolListPageRoute.handle} />}>
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <SchoolTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              schools={listItems}
              setSchools={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
